import type {
  Account,
  AdminAccountDetail,
  AdminAccountSummary,
  AdminAlbum,
  AdminAuditEntry,
  AdminDeviceDetail,
  AdminDeviceSummary,
  AdminOverview,
  AdminPasswordReset,
  AdminPhoto,
  AdminPhotoFilter,
  AdminScreen,
  AuthResult,
  Page,
} from '@mantelpiece/shared';

/**
 * Its own key, on its own origin. `admin.` never shares `localStorage` with
 * `app.`, so the operator's session here and their ordinary session there are
 * two sessions — signing out of one leaves the other alone, which is what an
 * operator who is also a user wants.
 */
const SESSION_KEY = 'mantelpiece.admin.session';

export interface StoredSession {
  readonly token: string;
  readonly account: Account;
}

export const loadSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.token !== 'string' || !parsed.account) return null;
    return { token: parsed.token, account: parsed.account };
  } catch {
    return null;
  }
};

export const saveSession = (session: StoredSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

export const clearSession = (): void => {
  localStorage.removeItem(SESSION_KEY);
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(
  path: string,
  init: RequestInit & { token?: string | undefined } = {},
): Promise<T> => {
  const { token, headers, ...rest } = init;
  const response = await fetch(`/v1${path}`, {
    ...rest,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | (T & { error?: string; message?: string })
    | null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error ?? 'unknown',
      payload?.message ?? 'Something went wrong. Try again.',
    );
  }
  return payload as T;
};

const json = (body: unknown, method = 'POST'): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

const qs = (params: object): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
};

export interface Paged {
  readonly offset?: number | undefined;
  readonly limit?: number | undefined;
}

export const api = {
  /* -------------------------------------------------------------- session -- */

  signIn: (email: string, password: string) =>
    request<AuthResult>('/auth/signin', json({ email, password })),

  signOut: (token: string) => request<void>('/auth/signout', { method: 'POST', token }),

  /** 403 for a real account that is not on the list — the sign-in page reads it. */
  whoAmI: (token: string) =>
    request<{ account: Pick<Account, 'id' | 'email' | 'name'> }>('/admin/me', { token }),

  /* ------------------------------------------------------------- overview -- */

  overview: (token: string) => request<AdminOverview>('/admin/overview', { token }),

  /* ------------------------------------------------------------- accounts -- */

  listAccounts: (token: string, params: Paged & { q?: string | undefined; sort?: string | undefined }) =>
    request<Page<AdminAccountSummary>>(`/admin/accounts${qs(params)}`, { token }),

  getAccount: (token: string, id: string) =>
    request<AdminAccountDetail>(`/admin/accounts/${id}`, { token }),

  editAccount: (token: string, id: string, fields: { name: string; email: string }) =>
    request<{ id: string; name: string; email: string }>(`/admin/accounts/${id}`, {
      ...json(fields, 'PATCH'),
      token,
    }),

  resetPassword: (token: string, id: string) =>
    request<AdminPasswordReset>(`/admin/accounts/${id}/password`, { method: 'POST', token }),

  unlockAccount: (token: string, id: string) =>
    request<void>(`/admin/accounts/${id}/unlock`, { method: 'POST', token }),

  signOutEverywhere: (token: string, id: string) =>
    request<{ sessionsRevoked: number }>(`/admin/accounts/${id}/signout`, {
      method: 'POST',
      token,
    }),

  /* --------------------------------------------------------------- frames -- */

  listDevices: (token: string, params: Paged & { q?: string | undefined; accountId?: string | undefined }) =>
    request<Page<AdminDeviceSummary>>(`/admin/devices${qs(params)}`, { token }),

  getDevice: (token: string, id: string) =>
    request<AdminDeviceDetail>(`/admin/devices/${id}`, { token }),

  /* --------------------------------------------------------------- photos -- */

  listPhotos: (
    token: string,
    params: Paged & {
      q?: string | undefined;
      filter?: AdminPhotoFilter | undefined;
      deviceId?: string | undefined;
      accountId?: string | undefined;
      assetId?: string | undefined;
    },
  ) => request<Page<AdminPhoto>>(`/admin/photos${qs(params)}`, { token }),

  takeDownPhoto: (token: string, id: string) =>
    request<void>(`/admin/photos/${id}`, { method: 'DELETE', token }),

  /* -------------------------------------------------------------- screens -- */

  listScreens: (token: string, params: Paged & { revoked?: string | undefined }) =>
    request<Page<AdminScreen>>(`/admin/screens${qs(params)}`, { token }),

  /* --------------------------------------------------------------- albums -- */

  listAlbums: (token: string, params: Paged & { q?: string | undefined }) =>
    request<Page<AdminAlbum>>(`/admin/albums${qs(params)}`, { token }),

  /* ---------------------------------------------------------------- audit -- */

  listAudit: (token: string, params: Paged) =>
    request<Page<AdminAuditEntry>>(`/admin/audit${qs(params)}`, { token }),
};
